import { cn } from "@tcg/ui";
import { Camera, CheckCircle2, ScanLine } from "lucide-react";
import { useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { usePos } from "@/app/context";
import { QrScanner } from "@/features/pickup/QrScanner";
import {
  detectSymbology,
  probeScan,
  type KeyStroke,
  type ScanProbe,
} from "@/lib/scanner/symbology";
import { saveScanner, useScannerSettings, type ScannerSettings } from "@/lib/scanner/settings";

/**
 * スキャナー (T07-34): tap the box and scan any code — what was read, the kind of code (guessed from
 * the content: JAN / cert / QR / the shop's label), whether it came as a scanner burst, and which
 * key the scanner sends at the end. A scanner that ends with **Tab** is detected and saved at once
 * (the scan fields then accept Tab too); a prefix the scanner adds is stripped from then on.
 * The camera test reads a QR code with the tablet camera (the pickup screen uses the same reader).
 */
export function ScannerTest() {
  const { t } = useTranslation();
  const { db } = usePos();
  const settings = useScannerSettings();
  const strokes = useRef<KeyStroke[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [probe, setProbe] = useState<ScanProbe | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [cameraCode, setCameraCode] = useState<string | null>(null);

  const finish = async () => {
    const result = probeScan(strokes.current);
    strokes.current = [];
    if (!result?.code) {
      return;
    }
    setProbe(result);
    const next: ScannerSettings = {
      suffix: result.suffix ?? settings.suffix,
      prefix: result.prefix || settings.prefix,
    };
    if (next.suffix !== settings.suffix || next.prefix !== settings.prefix) {
      await saveScanner(db, next);
      setSaved(
        next.suffix !== settings.suffix
          ? t("scanner.saved_suffix", { key: next.suffix })
          : t("scanner.saved_prefix", { prefix: next.prefix }),
      );
    } else {
      setSaved(null);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    const at = event.timeStamp || performance.now();
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      strokes.current.push({ key: event.key, at });
      void finish();
      return;
    }
    if (event.key.length === 1) {
      event.preventDefault();
      strokes.current.push({ key: event.key, at });
      // a scanner without an end key: the code is complete after a short pause
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => void finish(), 400);
    }
  };

  return (
    <section aria-labelledby="scanner-title" className="flex flex-col gap-4">
      <h2 id="scanner-title" className="text-2xl font-semibold">
        {t("scanner.title")}
      </h2>
      <p>{t("scanner.intro")}</p>
      <div
        role="textbox"
        aria-label={t("scanner.box")}
        aria-readonly="true"
        tabIndex={0}
        onKeyDown={onKeyDown}
        className="flex min-h-28 items-center justify-center gap-3 rounded-lg border-2 border-dashed p-4 text-xl outline-none focus:border-primary focus:bg-accent"
      >
        <ScanLine aria-hidden className="size-8" />
        {probe ? <strong className="break-all">{probe.code}</strong> : t("scanner.box_hint")}
      </div>
      {probe ? (
        <dl
          aria-label={t("scanner.result")}
          className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-lg"
        >
          <dt className="text-muted-foreground">{t("scanner.kind")}</dt>
          <dd>{t(`scanner.kind_${probe.symbology}`)}</dd>
          <dt className="text-muted-foreground">{t("scanner.end_key")}</dt>
          <dd>{probe.suffix ? t(`scanner.key_${probe.suffix}`) : t("scanner.key_none")}</dd>
          <dt className="text-muted-foreground">{t("scanner.prefix")}</dt>
          <dd>{probe.prefix ? `「${probe.prefix}」` : t("scanner.prefix_none")}</dd>
          <dt className="text-muted-foreground">{t("scanner.speed")}</dt>
          <dd className={cn(probe.burst ? "text-pos-ok" : "text-pos-problem")}>
            {probe.burst
              ? t("scanner.burst_ok", { ms: probe.maxGapMs })
              : t("scanner.burst_slow", { ms: probe.maxGapMs })}
          </dd>
        </dl>
      ) : null}
      {saved ? (
        <p role="status" className="flex items-center gap-2 text-lg text-pos-ok">
          <CheckCircle2 aria-hidden className="size-6" />
          {saved}
        </p>
      ) : null}
      <fieldset className="flex flex-wrap items-center gap-2">
        <legend className="mb-1 font-medium">{t("scanner.end_key")}</legend>
        {(["Enter", "Tab"] as const).map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={settings.suffix === key}
            onClick={() => void saveScanner(db, { ...settings, suffix: key })}
            className={cn(
              "min-h-pos-touch rounded-md border px-6 text-lg",
              settings.suffix === key && "border-primary bg-primary text-primary-foreground",
            )}
          >
            {t(`scanner.key_${key}`)}
          </button>
        ))}
        {settings.prefix ? (
          <button
            type="button"
            onClick={() => void saveScanner(db, { ...settings, prefix: "" })}
            className="min-h-pos-touch rounded-md border px-5 text-lg"
          >
            {t("scanner.clear_prefix", { prefix: settings.prefix })}
          </button>
        ) : null}
      </fieldset>
      <div className="flex flex-col gap-2">
        <h3 className="flex items-center gap-2 text-lg font-semibold">
          <Camera aria-hidden className="size-5" />
          {t("scanner.camera_test")}
        </h3>
        <QrScanner onCode={(code) => setCameraCode(code)} />
        {cameraCode ? (
          <p role="status" className="text-lg">
            {t("scanner.camera_read", {
              code: cameraCode,
              kind: t(`scanner.kind_${detectSymbology(cameraCode)}`),
            })}
          </p>
        ) : null}
      </div>
    </section>
  );
}
